import React, { useState } from 'react';
import { UnifiedSlot } from '../InstructorDashboard';
import AuthenticationService from '../../../_services/AuthenticationService';
import RecordedVideoModal from './RecordedVideoModal';

// OnlineMeetingType codes from Tuningfork Constants.StudyResourceConstant.
const ONLINE_MEETING_ZOOM = 44;
const ONLINE_MEETING_TEAMS = 45;

// UserType.INSTRUCTOR from Tuningfork Constants — instructors should not see the
// Timetable button on the My Teaching Plan page.
const USER_TYPE_INSTRUCTOR = 4;

interface SlotActionBarProps {
  slot: UnifiedSlot;
  compact?: boolean;
  // Extra params carry the slot's instructor info so the admin flow can build
  // a /attendance-node URL that includes ?instructorId=&instructorName= — the
  // attendance page validates the instructor→batch mapping against the URL, not
  // against the logged-in user, so an admin viewing someone else's slot MUST
  // pass these. The instructor's own /my-teaching-plan flow accepts them
  // harmlessly (they duplicate the session user).
  onAttendance: (
    batchIds: number[],
    templateSlotId?: number,
    instructorId?: number,
    instructorName?: string,
  ) => void;
  onLessonPlan: (batchId: number, lessonPlanId: number, topicId?: number, instructorId?: number, activitySlotTemplateId?: number) => void;
  onViewTemplate: (templateId: number) => void;
  onConfirm?: (slot: UnifiedSlot) => void;
  onUnconfirm?: (slot: UnifiedSlot) => void;
  /**
   * Force-hide the Timetable button regardless of user type. Used inside
   * TemplateConductedView — we're already on the timetable's detail page, so
   * the button would just navigate to the current URL.
   */
  hideViewTemplateButton?: boolean;
}

const SlotActionBar: React.FC<SlotActionBarProps> = ({
  slot,
  compact = false,
  onAttendance,
  onLessonPlan,
  onViewTemplate,
  onConfirm,
  onUnconfirm,
  hideViewTemplateButton = false,
}) => {
  const canTakeAttendance = slot.batchIds.length > 0 &&
    slot.status !== 'conducted' &&
    slot.status !== 'cancelled';

  const hasLessonPlan = slot.lessonPlanId !== null &&
    slot.batchIds.length > 0;

  // Online Class button is shown when the slot has any online-meeting metadata.
  // ZoomMeetingId is populated for both Zoom and Teams meetings; for Teams the
  // backend additionally provides onlineJoinUrl parsed from ZoomMeetingData JSON.
  const hasOnlineMeeting = !!(slot.zoomMeetingId || slot.onlineJoinUrl);

  // Hide the Timetable button when the logged-in user is an instructor (userTypeId=4)
  // — matches the Java JSP behaviour driven by the `instructorLogin` request attribute.
  // Also honour hideViewTemplateButton for callers that render this bar inside a
  // page that IS the timetable detail (e.g. TemplateConductedView).
  const currentUserTypeId = AuthenticationService?.currentUserValue?.userTypeId;
  const showTimetableButton = !hideViewTemplateButton && currentUserTypeId !== USER_TYPE_INSTRUCTOR;

  const [showRecordedVideoModal, setShowRecordedVideoModal] = useState(false);

  const handleAttendance = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAttendance(
      slot.batchIds,
      slot.templateSlotId,
      slot.instructorId,
      slot.instructorName,
    );
  };

  const handleLessonPlan = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (slot.batchIds.length > 0 && slot.lessonPlanId) {
      onLessonPlan(slot.batchIds[0], slot.lessonPlanId, slot.topicMasterId || undefined, slot.instructorId, slot.templateSlotId);
    }
  };

  const handleViewTemplate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewTemplate(slot.templateId);
  };

  const handleRecordedVideo = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRecordedVideoModal(true);
  };

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onConfirm) onConfirm(slot);
  };

  const handleUnconfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onUnconfirm) onUnconfirm(slot);
  };

  // Confirmation is an instructor's "I agree to teach this slot" signal — it's
  // meaningful at any point until the slot is cancelled. Don't gate on status
  // beyond that: the scheduler can still ask for confirmation on a scheduled
  // slot, and an instructor may want to retro-confirm an already-conducted one.
  const isConfirmed = slot.confirmedByUserId != null;
  const slotIsLive = slot.status !== 'cancelled';
  const showConfirmAction = !!onConfirm && !isConfirmed && slotIsLive;
  const showUnconfirmAction = !!onUnconfirm && isConfirmed && slotIsLive;

  const handleOnlineMeeting = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Teams: use the joinUrl parsed from ZoomMeetingData on the server.
    if (slot.onlineMeetingType === ONLINE_MEETING_TEAMS && slot.onlineJoinUrl) {
      window.open(slot.onlineJoinUrl, '_blank');
      return;
    }
    // Zoom: build the join URL from ZoomMeetingId (default behaviour).
    if (slot.zoomMeetingId) {
      window.open(`https://zoom.us/j/${slot.zoomMeetingId}`, '_blank');
      return;
    }
    // Fallback: if the row has only a join URL (some Teams rows might lack a
    // distinct meeting id), open it directly.
    if (slot.onlineJoinUrl) {
      window.open(slot.onlineJoinUrl, '_blank');
    }
  };

  // Recorded Video button shows whenever a slot has online-meeting metadata —
  // the modal itself reports "No videos available" if nothing's mapped yet.
  const showRecordedVideoButton = hasOnlineMeeting;

  const recordedVideoModalNode = (
    <RecordedVideoModal
      show={showRecordedVideoModal}
      onClose={() => setShowRecordedVideoModal(false)}
      activitySlotTemplateId={slot.templateSlotId}
      batchId={slot.batchIds[0] ?? null}
      topicName={slot.topicName}
    />
  );

  if (compact) {
    return (
      <>
        <div className="idash-action-bar compact">
          {showConfirmAction && (
            <button
              className="action-btn action-confirm"
              onClick={handleConfirm}
              title="Confirm this slot — needed before students can see it"
            >
              ✓
            </button>
          )}
          {showUnconfirmAction && (
            <button
              className="action-btn action-unconfirm"
              onClick={handleUnconfirm}
              title="Revoke confirmation"
            >
              ✗
            </button>
          )}
          {canTakeAttendance && (
            <button
              className="action-btn action-attendance"
              onClick={handleAttendance}
              title="Take Attendance"
            >
              A
            </button>
          )}
          {hasOnlineMeeting && (
            <button
              className="action-btn action-zoom"
              onClick={handleOnlineMeeting}
              title={slot.onlineMeetingType === ONLINE_MEETING_TEAMS ? 'Start Teams Meeting' : 'Start Zoom Meeting'}
            >
              {slot.onlineMeetingType === ONLINE_MEETING_TEAMS ? 'T' : 'Z'}
            </button>
          )}
          {showRecordedVideoButton && (
            <button
              className="action-btn action-recorded-video"
              onClick={handleRecordedVideo}
              title="Recorded Video"
            >
              RV
            </button>
          )}
          {hasLessonPlan && (
            <button
              className="action-btn action-lesson-plan"
              onClick={handleLessonPlan}
              title="Open Lesson Plan"
            >
              LP
            </button>
          )}
        </div>
        {recordedVideoModalNode}
      </>
    );
  }

  return (
    <>
      <div className="idash-action-bar">
        {showConfirmAction && (
          <button
            className="action-btn action-confirm"
            onClick={handleConfirm}
            title="Confirm this slot — needed before students can see it"
          >
            <span aria-hidden="true">✓</span> Confirm
          </button>
        )}
        {showUnconfirmAction && (
          <button
            className="action-btn action-unconfirm"
            onClick={handleUnconfirm}
            title="Revoke confirmation"
          >
            Unconfirm
          </button>
        )}
        <button
          className={`action-btn action-attendance ${!canTakeAttendance ? 'disabled' : ''}`}
          onClick={canTakeAttendance ? handleAttendance : undefined}
          disabled={!canTakeAttendance}
          title={canTakeAttendance ? 'Take Attendance' : 'Attendance not available (slot not scheduled)'}
        >
          Attendance
        </button>
        {hasOnlineMeeting && (
          <button
            className="action-btn action-zoom"
            onClick={handleOnlineMeeting}
            title={slot.onlineMeetingType === ONLINE_MEETING_TEAMS ? 'Start Teams Meeting' : 'Start Zoom Meeting'}
          >
            Online Class
          </button>
        )}
        {showRecordedVideoButton && (
          <button
            className="action-btn action-recorded-video"
            onClick={handleRecordedVideo}
            title="Recorded Video"
          >
            Recorded Video
          </button>
        )}
        {hasLessonPlan && (
          <button
            className="action-btn action-lesson-plan"
            onClick={handleLessonPlan}
            title="Open Lesson Plan"
          >
            Lesson Plan
          </button>
        )}
        {showTimetableButton && (
          <button
            className="action-btn action-template"
            onClick={handleViewTemplate}
            title="View Timetable"
          >
            Timetable
          </button>
        )}
      </div>
      {recordedVideoModalNode}
    </>
  );
};

export default SlotActionBar;
